import React, { useEffect, useState } from "react";
import { Plan } from "../../../@types";
import StarRatingRow from "./StarRatingRow";
import StarRatingList from "./StarRatingList";
import StarRatingListItem from "./StarRatingListItem";
import { getStarRating } from "../../../helpers/starRatingHelpers";
import { useTranslate } from "../../../helpers/intlHooks";
import { sendTealiumEvent } from "../../../helpers";
import { StarRating } from "../../../components";

const DrugStarRatings: React.FunctionComponent<{
  plan: Plan;
  expandAll?: boolean;
}> = ({ plan, expandAll = false }) => {
  const t = useTranslate();
  const { contract_year } = plan;
  const notCY22 = contract_year !== "2022";
  const [drugPlanExpanded, setDrugPlanExpanded] = useState(expandAll);
  const [customerServiceExpanded, setCustomerServiceExpanded] = useState(
    expandAll
  );
  const [complaintsExpanded, setComplaintsExpanded] = useState(expandAll);
  const [memberExperienceExpanded, setMemberExperienceExpanded] = useState(
    expandAll
  );
  const [safetyExpanded, setSafetyExpanded] = useState(expandAll);

  const toggleDrugPlanExpanded = (): void => {
    setDrugPlanExpanded(!drugPlanExpanded);

    if (!drugPlanExpanded) {
      setCustomerServiceExpanded(false);
      setComplaintsExpanded(false);
      setMemberExperienceExpanded(false);
      setComplaintsExpanded(false);
      setSafetyExpanded(false);

      sendTealiumEvent(
        "Plan Details - Star Ratings",
        "Toggle - Drug Plan Star Ratings"
      );
    }
  };

  useEffect(() => {
    setDrugPlanExpanded(expandAll);
    setCustomerServiceExpanded(expandAll);
    setComplaintsExpanded(expandAll);
    setMemberExperienceExpanded(expandAll);
    setComplaintsExpanded(expandAll);
    setSafetyExpanded(expandAll);
  }, [expandAll]);

  return (
    <div data-testid="drug-plan-ratings">
      <StarRatingRow
        label={t("plan_stars.drug.star_ratings")}
        open={drugPlanExpanded}
        onClick={toggleDrugPlanExpanded}
        color="green"
        plan={plan}
        ariaLabel={t("star_rating.drug_plan_aria")}
      >
        {/* Customer Service */}
        <StarRatingRow
          label={t("plan_stars.drug.customer_service")}
          open={customerServiceExpanded}
          starRating={getStarRating(plan, "STAR_DRUG_CUSTOMER_SERVICE_RATING")}
          onClick={(): void => {
            setCustomerServiceExpanded(!customerServiceExpanded);
            if (!customerServiceExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan customer service"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.drug.customer_service.tty")}
              starRating={getStarRating(plan, "STAR_DRUG_TTY_AND_FL_RATING")}
              planYear={plan.contract_year}
            />
            {notCY22 && (
              <StarRatingListItem
                label={t("plan_stars.drug.customer_service.timely_decisions")}
                starRating={getStarRating(plan, "STAR_DRUG_APPEALS_RATING")}
                planYear={plan.contract_year}
              />
            )}

            {notCY22 && (
              <StarRatingListItem
                label={t("plan_stars.drug.customer_service.fair_appeals")}
                starRating={getStarRating(
                  plan,
                  "STAR_DRUG_APPEALS_FAIR_RATING"
                )}
                planYear={plan.contract_year}
              />
            )}
          </StarRatingList>
        </StarRatingRow>

        {/* Member Complaints */}
        <StarRatingRow
          label={t("plan_stars.drug.complaints")}
          open={complaintsExpanded}
          starRating={getStarRating(plan, "STAR_DRUG_COMPLAINTS_RATING")}
          onClick={(): void => {
            setComplaintsExpanded(!complaintsExpanded);
            if (!complaintsExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan member complaints"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.drug.complaints.complaints")}
              starRating={getStarRating(
                plan,
                "STAR_DRUG_PLAN_COMPLAINTS_RATING"
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.complaints.leaving")}
              starRating={getStarRating(
                plan,
                "STAR_DRUG_MEMBERS_LEAVING_RATING"
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.complaints.improvement")}
              starRating={getStarRating(plan, "STAR_DRUG_IMPROVEMENT_RATING")}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Member Experience */}
        <StarRatingRow
          label={t("plan_stars.drug.experience")}
          open={memberExperienceExpanded}
          starRating={getStarRating(plan, "STAR_DRUG_MEMBER_EXP_RATING")}
          onClick={(): void => {
            setMemberExperienceExpanded(!memberExperienceExpanded);
            if (!memberExperienceExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan member experience"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.drug.experience.rating")}
              starRating={getStarRating(plan, "STAR_DRUG_PLAN_MEMBER_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.experience.prescription")}
              starRating={getStarRating(plan, "STAR_PRESCRIPTION_RATING")}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Drufy Safety and Pricing */}
        <StarRatingRow
          label={t("plan_stars.drug.safety")}
          open={safetyExpanded}
          starRating={getStarRating(plan, "STAR_PROVIDES_PRICING_RATING")}
          onClick={(): void => {
            setSafetyExpanded(!safetyExpanded);
            if (!safetyExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan safety and pricing"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.drug.safety.pricing")}
              starRating={getStarRating(plan, "STAR_DRUG_ACCURACY_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.safety.diabetes")}
              starRating={getStarRating(plan, "STAR_DIABETES_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.safety.blood_pressure")}
              starRating={getStarRating(plan, "STAR_BLOOD_PRESSURE_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.safety.cholesterol")}
              starRating={getStarRating(plan, "STAR_CHOLESTEROL_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.safety.pharmacist")}
              starRating={getStarRating(plan, "STAR_PHARMACIST_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.drug.safety.diabetes_cholesterol")}
              starRating={getStarRating(
                plan,
                "STAR_DIABETES_CHOLESTEROL_RATING"
              )}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>
      </StarRatingRow>

      {/* Summary */}
      <div className={`StarRatingRow StarRatingRow__blue`}>
        <h3 className={"StarRatingsSection__heading"}>
          {t("plan_stars.drug.summary")}
        </h3>
        <StarRating
          stars={getStarRating(plan, "STAR_DRUG_PLAN_RATING")}
          includeLabel={false}
          planYear={plan.contract_year}
        />
      </div>
    </div>
  );
};

export default DrugStarRatings;
